import { useState, useEffect } from "react";
import { Container, Typography, Grid, TextField } from "@mui/material";
import { Helmet } from "react-helmet";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";
import { withSnackbar } from "notistack";
import { Box, Button } from "@mui/material";
import { useNewPatientContext } from "../store/NewPatientContextProvider";
import {
  updateStudyWithAccession,
  getProcedures,
  getUsersByRole,
} from "../api/api";
import { useHistory } from "react-router-dom";
import { CircleSpinner } from "react-spinners-kit";
import { useDataLists } from "../store/DataListsContextProvider";
import { setPatientToInit } from "../store/action-reducers";
import { v4 as uuidv4 } from "uuid";

function CreateStudy(props) {
  const { lists } = useDataLists();
  const history = useHistory();
  if (!localStorage.getItem("current-procedure"))
    history.push("/global-worklist");
  const { newPatient, newPatientDispatch } = useNewPatientContext();
  const [isSaving, setSaving] = useState(false);
  const [remoteStudyRouteDate, setRemoteStudyRouteDate] = useState({
    checked: false,
    users: {
      radiographers: [],
      radiologists: [],
      receptionists: [],
      refDoctors: [],
    },
    procedures: [],
  });
  const [form, setForm] = useState(
    JSON.parse(localStorage.getItem("current-procedure"))
  );
  const [chosenProcedure, setChosenProcedure] = useState(null);
  const [date, setDate] = useState(new Date(form.ExamDate));

  useEffect(() => {
    if (!newPatient.PatientDetails.SessionID) {
      history.push("patient");
      return;
    }

    const getPageRemoteData = async () => {
      const [
        radiographer_res,
        radiologist_res,
        receptionist_res,
        ref_doctor_res,
        procedures_res,
      ] = await Promise.all([
        getUsersByRole("radiographer"),
        getUsersByRole("radiologist"),
        getUsersByRole("receptionist"),
        getUsersByRole("ref-doctor"),
        getProcedures(),
      ]);
      const [radiographer_data, radiographer_err] = radiographer_res;
      const [radiologist_data, radiologist_err] = radiologist_res;
      const [receptionist_data, receptionist_err] = receptionist_res;
      const [ref_doctor_data, ref_doctor_err] = ref_doctor_res;
      const [procedures_data, procedures_err] = procedures_res;

      if (radiographer_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RADIOGRAPHER LIST : ${radiographer_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (radiologist_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RADIOLOGISTS LIST : ${radiologist_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (receptionist_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING RECEPTIONIST LIST : ${receptionist_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (ref_doctor_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING REF-DOCTOR LIST : ${ref_doctor_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      } else if (procedures_err) {
        props.enqueueSnackbar(
          `ERROR FETCHING PROCEDURE LIST : ${procedures_err.message}`,
          {
            preventDuplicate: true,
            variant: "error",
          }
        );
        return;
      }

      const payload = {
        users: {
          radiographers: radiographer_data.data,
          radiologists: radiologist_data.data,
          receptionists: receptionist_data.data,
          refDoctors: ref_doctor_data.data,
        },
        procedures: procedures_data.data,
        checked: true,
      };

      setRemoteStudyRouteDate(payload);
    };

    getPageRemoteData();
  }, []);

  useEffect(() => {
    setForm({
      ...form,
      Schedule: new Date(date).getTime(),
      Timestamp: new Date().getTime(),
    });
  }, [date]);

  useEffect(() => {
    const chosenProcedure = lists.procedures.find(
      (proc) => form.Procedure === proc.Name
    );
    setChosenProcedure(chosenProcedure);
  }, [form.Procedure, lists.procedures]);

  const handleInputChange = (e, isProcedureCode) => {
    if (isProcedureCode) {
      let chosenProcedure = lists.procedures.find(
        (proc) => proc.Prefix === e.target.value
      );

      setForm({
        ...form,
        [e.target.name]: chosenProcedure.Name,
      });
    } else {
      setForm({
        ...form,
        [e.target.name]: e.target.value,
      });
    }
  };

  const handleSubmit = async () => {
    setSaving(true);

    if (!form.Schedule) {
      props.enqueueSnackbar("PLEASE SELECT A SCHEDULE DATE", {
        preventDuplicate: true,
        variant: "error",
      });
      setSaving(false);
      return;
    }

    try {
      let newStudy = form;
      console.log("before newStudy", newStudy);
      const oldStudy = JSON.parse(localStorage.getItem("current-procedure"));
      if (newStudy.Branch != oldStudy.Branch) {
        newStudy.Accession = newStudy.Branch + newStudy.AccessionNumber;
        console.log("New accession newStudy", newStudy);
      }
      console.log("After newStudy", newStudy);
      const [, err] = await updateStudyWithAccession(
        newStudy,
        oldStudy.Accession
      );

      if (err) {
        props.enqueueSnackbar(err.message, {
          preventDuplicate: true,
          variant: "error",
        });
        setSaving(false);
      } else {
        props.enqueueSnackbar("STUDY UPDATED SUCCESSFULLY", {
          preventDuplicate: true,
          variant: "success",
        });
        setSaving(false);
        localStorage.setItem("current-procedure", "");
        history.push("/worklist");
      }
    } catch (err) {
      props.enqueueSnackbar(err.message, {
        preventDuplicate: true,
        variant: "error",
      });
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit Study</title>
      </Helmet>

      <Container style={{ padding: 20 }} maxWidth="md">
        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 3, color: "#fff", opacity: 0.8 }}
          component="div"
        >
          Patient
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="First Name"
              variant="outlined"
              name="FirstName"
              disabled={true}
              value={form.FirstName}
              fullWidth
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Last Name"
              variant="outlined"
              name="LastName"
              disabled={true}
              value={form.LastName}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Title"
              variant="outlined"
              name="Title"
              disabled={true}
              value={form.Title}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <TextField
              label="Session ID"
              variant="outlined"
              name="SessionID"
              disabled={true}
              value={form.SessionID}
              onChange={handleInputChange}
              fullWidth
            />
          </Grid>

          {Boolean(lists.branches.length) && (
            <Grid item xs={4}>
              {form.Status == "WAITING" || form.Status == "ARRIVED" ? (
                <TextField
                  select
                  label="Branch"
                  name="Branch"
                  id="Branch"
                  onChange={handleInputChange}
                  value={form.Branch}
                  SelectProps={{
                    native: true,
                  }}
                  fullWidth
                >
                  {lists.branches.map((branch) => {
                    return (
                      <option value={branch.Prefix} key={uuidv4()}>
                        {branch.Name}
                      </option>
                    );
                  })}
                </TextField>
              ) : (
                <TextField
                  select
                  label="Branch"
                  name="Branch"
                  id="Branch"
                  disabled
                  onChange={handleInputChange}
                  value={form.Branch}
                  SelectProps={{
                    native: true,
                  }}
                  fullWidth
                >
                  {lists.branches.map((branch) => {
                    return (
                      <option value={branch.Prefix} key={uuidv4()}>
                        {branch.Name}
                      </option>
                    );
                  })}
                </TextField>
              )}
            </Grid>
          )}

          {/* <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={date}
                label="Schedule"
                onChange={(newValue) => {
                  setDate(newValue);
                }}
                renderInput={(params) => <TextField fullWidth {...params} />}
              />
            </LocalizationProvider>
          </Grid> */}

          {!remoteStudyRouteDate.checked && (
            <Grid item md={12}>
              <Box
                sx={{
                  p: 4,
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CircleSpinner />
              </Box>
            </Grid>
          )}

          {Boolean(remoteStudyRouteDate.users.receptionists.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Receptionist Name"
                name="Receptionist"
                onChange={handleInputChange}
                value={form.AssignedReceptionist}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteDate.users.receptionists.map(
                  (receptionist, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={receptionist.UserName}
                      >
                        {receptionist.UserName}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteDate.users.radiographers.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Radiographer Name"
                name="Radiographer"
                onChange={handleInputChange}
                value={form.AssignedRadiographer}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                <option value={`NONE`}>NONE</option>
                {remoteStudyRouteDate.users.radiographers.map(
                  (radiographer, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={radiographer.UserName}
                      >
                        {`${radiographer.FirstName} ${radiographer.LastName}`}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteDate.users.radiologists.length) && (
            <Grid item xs={4}>
              <TextField
                select
                label="Radiologist Name"
                name="Radiologist"
                value={form.AssignedRadiologist}
                onChange={handleInputChange}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                <option value={`NONE`}>NONE</option>
                {remoteStudyRouteDate.users.radiologists.map(
                  (radiologist, index) => {
                    return (
                      <option
                        key={"option-" + index}
                        value={radiologist.UserName}
                      >
                        {`DR ${radiologist.FirstName} ${radiologist.LastName}`}
                      </option>
                    );
                  }
                )}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteDate.users.refDoctors.length) && (
            <Grid item xs={6}>
              <TextField
                select
                label="Ref doctor"
                name="RefDoctor"
                onChange={handleInputChange}
                value={form.AssignedRefDoctor}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteDate.users.refDoctors.map((doctor, index) => {
                  return (
                    <option key={"option-" + index} value={doctor.UserName}>
                      {`Dr ${doctor.FirstName} ${doctor.UserName}`}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}

          {Boolean(remoteStudyRouteDate.users.refDoctors.length) && (
            <Grid item xs={6}>
              <TextField
                select
                label="Secondary Ref doctor"
                name="SecondaryRefDoctor"
                onChange={handleInputChange}
                value={form.AssignedRefDoctor}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {remoteStudyRouteDate.users.refDoctors.map((doctor, index) => {
                  return (
                    <option key={"option-" + index} value={doctor.UserName}>
                      {`Dr ${doctor.FirstName} ${doctor.UserName}`}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}
        </Grid>

        <br />

        <Typography
          variant="h5"
          gutterBottom
          sx={{ mb: 3, color: "#fff", opacity: 0.8 }}
          component="div"
        >
          Procedure
        </Typography>

        <Grid container spacing={1}>
          {Boolean(lists.procedures.length) && chosenProcedure && (
            <Grid item xs={6}>
              <TextField
                select
                label="Select procedure by code"
                name="Procedure"
                value={chosenProcedure.Prefix}
                onChange={(e) => handleInputChange(e, "IS_PROCEDURE_CODE")}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {lists.procedures.map((procedure, index) => {
                  return (
                    <option key={"option-" + index} value={procedure.Prefix}>
                      {procedure.Prefix}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}

          {Boolean(lists.procedures.length) && chosenProcedure && (
            <Grid item xs={6}>
              <TextField
                select
                label="Select procedure by name"
                name="Procedure"
                value={chosenProcedure.Name}
                onChange={handleInputChange}
                SelectProps={{
                  native: true,
                }}
                fullWidth
              >
                {lists.procedures.map((procedure, index) => {
                  return (
                    <option key={"option-" + index} value={procedure.Name}>
                      {procedure.Name}
                    </option>
                  );
                })}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                size="large"
                onClick={() => {
                  newPatientDispatch(setPatientToInit());
                  history.push("/");
                }}
                disabled={isSaving}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>

              <Button
                color="secondary"
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default withSnackbar(CreateStudy);
